/**
 * index.js — /vin-decoder.
 *
 * Тул отвечает ровно на то, что закодировано в семнадцати знаках, и отдельно
 * называет то, чего в них нет. Второе здесь так же важно, как первое: весь
 * сегмент «проверка по VIN» построен на обещании истории и пробега, которых в
 * самом номере не бывает — они берутся из сторонних баз, а номер лишь ключ.
 *
 * Полная таблица изготовителей с замерами (public/data/vin/wmi.json) грузится
 * сразу при открытии страницы, а разбор ждёт её не дольше DATA_WAIT_MS: не
 * пришла — разбор идёт по ядру из vin.js, а когда таблица дойдёт, ответ
 * перерисовывается сам.
 */

import { decode, normalize, frameNumber, useWmiData, hasWmiData, CONFUSABLE, formatMaker as formatMakerName, makerCountry } from './vin.js';

const SLUG = 'vin-decoder';
const DATA_WAIT_MS = 6000;

/* Ссылки источников — адреса, а не текст: подпись к ним в переводах. */
const SRC = {
    wikibooks: 'https://en.wikibooks.org/wiki/Vehicle_Identification_Numbers_(VIN_codes)/World_Manufacturer_Identifier_(WMI)',
    iso: 'https://standards.iso.org/iso/3780/',
    registry: 'https://data.gov.ua/dataset/0ffd8b75-0628-48cc-952a-9302f9799ec0',
};

let ui = null;
let dataP = null;
let corpusCars = 0;
let lastRaw = null;

const $ = (id) => document.getElementById(id);
const t = (k, sub) => {
    let s = (ui && ui.lang && ui.lang[k] != null) ? ui.lang[k] : k;
    if (sub) for (const p in sub) s = String(s).split(':' + p).join(sub[p]);
    return s;
};
/* Воронка. `__stage` сам считает время на шаге и досылает `final_stage`,
   когда человек уходит со страницы, — то есть отвечает на вопрос «где
   отваливаются», а не «сколько нажали». Слаг берётся из <meta name="tool-slug">,
   передавать его не надо. */
const stage = (n, d) => { try { window.__stage && window.__stage(n, d || {}); } catch (e) {} };
const ae = (ev, d) => { try { window.__ae && window.__ae(ev, Object.assign({ slug: SLUG }, d || {})); } catch (e) {} };
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/** Число по-местному (51 030 / 51,030); `ar` — латинские цифры (см. __intlLocale). */
function num(n) {
    try {
        const loc = window.__intlLocale ? window.__intlLocale() : undefined;
        return new Intl.NumberFormat(loc).format(n);
    } catch (e) { return String(n); }
}
function pct(p) {
    try {
        const loc = window.__intlLocale ? window.__intlLocale() : undefined;
        return new Intl.NumberFormat(loc, { maximumFractionDigits: 1 }).format(p);
    } catch (e) { return String(p); }
}

/** Таблица изготовителей: один запрос на страницу, разбор её ждёт. */
function loadData() {
    if (dataP) return dataP;
    const url = ui && ui.cfg && ui.cfg.dataUrl;
    if (!url) return (dataP = Promise.resolve(false));
    dataP = fetch(url, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => {
            if (!j || !useWmiData(j)) return false;
            corpusCars = (j.corpus && j.corpus.cars) || 0;
            return true;
        })
        .catch(() => false);
    return dataP;
}
function waitData() {
    const p = loadData();
    if (hasWmiData()) return Promise.resolve(true);
    const capped = window.__bounded ? window.__bounded(p, DATA_WAIT_MS, 'vin_wmi_data')
        : Promise.race([p, new Promise((r) => setTimeout(() => r(false), DATA_WAIT_MS))]);
    return capped.catch(() => false);
}

/**
 * Название изготовителя на языке страницы — общий помощник vin.js (им же
 * пользуется /car-check). На английской странице до 10.09.2026 первый же
 * образец отвечал «Honda (США)» — 109 записей из 272 таблицы несли русскую
 * строку; теперь уточнение — токен, и переводит его страница.
 */
const formatMaker = (raw) => formatMakerName(raw, t);

/** Разбивка номера на части с подсветкой — самая наглядная часть ответа. */
function renderRuler(parts) {
    return `<div class="vd-ruler">${parts.map((p) =>
        `<div class="vd-seg vd-seg-${p.cls}"><span class="vd-seg-chars">${esc(p.text)}</span>`
        + `<span class="vd-seg-label">${esc(p.label)}</span></div>`).join('')}</div>`;
}

function measuredLine(key, m) {
    return m ? `<div class="vd-fine">${t(key, { pct: pct(m.pct), n: num(m.n) })}</div>` : '';
}

function renderChecksum(r) {
    const c = r.checksum;
    if (c.state === 'ok') {
        return `<div class="vd-card vd-ok">
            <div class="vd-card-h">✅ ${t('chk_ok_title')}</div>
            <div class="vd-card-b">${t('chk_ok_body')}</div>
            ${measuredLine(c.use === 'mixed' ? 'chk_measured_mixed' : 'chk_measured_used', c.measured)}
            <div class="vd-fine">${t('chk_limits')}</div></div>`;
    }
    if (c.state === 'bad') {
        return `<div class="vd-card vd-bad">
            <div class="vd-card-h">⚠️ ${t('chk_bad_title')}</div>
            <div class="vd-card-b">${t('chk_bad_body', { expected: c.expected, actual: c.actual })}</div>
            <div class="vd-fine">${t('chk_bad_hint')}</div></div>`;
    }
    if (c.state === 'bad_soft') {
        return `<div class="vd-card vd-warn">
            <div class="vd-card-h">⚠️ ${t('chk_bad_title')}</div>
            <div class="vd-card-b">${t('chk_bad_body', { expected: c.expected, actual: c.actual })}</div>
            <div class="vd-fine">${t('chk_soft_hint', { pct: pct(c.measured.pct), n: num(c.measured.n) })}</div></div>`;
    }
    if (c.state === 'mixed') {
        return `<div class="vd-card vd-neutral">
            <div class="vd-card-h">ℹ️ ${t('chk_mixed_title')}</div>
            <div class="vd-card-b">${t('chk_mixed_body', { expected: c.expected, actual: c.actual, pct: pct(c.measured.pct), n: num(c.measured.n) })}</div></div>`;
    }
    if (c.state === 'uncomputable') return '';
    const body = c.measured
        ? t('chk_na_measured', { pct: pct(c.measured.pct), n: num(c.measured.n) })
        : t('chk_na_body');
    return `<div class="vd-card vd-neutral">
        <div class="vd-card-h">ℹ️ ${t('chk_na_title')}</div>
        <div class="vd-card-b">${body}</div>
        <div class="vd-fine">${t('chk_na_hint', { expected: c.expected || '—' })}</div></div>`;
}

function yearRow(value, mono) {
    return `<div class="vd-row"><span class="vd-k">${t('f_year')}</span>
        <span class="vd-v${mono ? ' vd-mono' : ''}">${value}</span></div>`;
}

function renderYear(r) {
    const y = r.year;
    if (!y) return '';
    const shown = (y.certain ? [y.years[0]] : y.both).join(' ' + t('or') + ' ');
    if (!y.isCode) {
        return yearRow(esc(t('f_year_none'))) + `<div class="vd-fine">${t('year_not_code', { c: esc(y.code) })}</div>`;
    }
    if (y.policy === 'unreliable') {
        const ifLine = y.measured && y.measured.pct >= 50 ? ' ' + t('year_if', { years: shown }) : '';
        return yearRow(esc(t('f_year_none')))
            + `<div class="vd-fine">${t('year_unreliable', { pct: pct(y.measured.pct), n: num(y.measured.n) })}${ifLine}</div>`;
    }
    let out = yearRow(esc(shown));
    if (y.cut) out += `<div class="vd-fine">${t('year_cycle_cut', { other: y.cut, y0: y.y0 })}</div>`;
    else out += `<div class="vd-fine">${y.certain ? t('year_certain') : t('year_ambiguous')}</div>`;
    if (y.policy === 'unmeasured') out += `<div class="vd-fine">${t('year_unmeasured')}</div>`;
    else out += measuredLine('year_measured_ok', y.measured);
    return out;
}

function renderSources() {
    const cars = corpusCars ? num(corpusCars) : '';
    return `<div class="vd-sources"><div class="vd-sources-h">${t('sources_title')}</div><ul>
        <li>${t('src_iso')} — <a href="${SRC.iso}" target="_blank" rel="noopener">standards.iso.org</a></li>
        <li>${t('src_wmi')} — <a href="${SRC.wikibooks}" target="_blank" rel="noopener">en.wikibooks.org</a></li>
        ${cars ? `<li>${t('src_measure', { cars })} — <a href="${SRC.registry}" target="_blank" rel="noopener">data.gov.ua</a></li>` : ''}
    </ul></div>`;
}

function renderFrame(r) {
    const f = r.frame;
    ui.out.innerHTML = renderRuler([
        { cls: 'wmi', text: f.model, label: t('part_model') },
        { cls: 'ser', text: f.serial, label: t('part_serial') },
    ]) + `<div class="vd-card vd-neutral"><div class="vd-card-h">ℹ️ ${t('frame_title')}</div>
            <div class="vd-card-b">${t('frame_body', { model: esc(f.model), serial: esc(f.serial) })}</div>
            <div class="vd-fine">${t('frame_hint')}</div></div>`
        + `<div class="vd-card vd-warn"><div class="vd-card-h">${t('not_in_vin_title')}</div>
            <div class="vd-card-b">${t('not_in_vin_body')}</div></div>`;
    ui.out.hidden = false;
    stage('frame');
    ae('feature_use', { action: 'frame', model_len: f.model.length });
}

function render(raw) {
    lastRaw = raw;
    try { renderGarageAct(raw); } catch (e) { /* the decode comes first */ }
    const r = decode(raw);

    if (r.kind === 'frame') { renderFrame(r); return; }

    if (!r.valid) {
        const bits = [`<div class="vd-card vd-bad"><div class="vd-card-h">⚠️ ${t('invalid_title')}</div><ul class="vd-list">`];
        for (const i of r.issues) {
            if (i === 'empty') continue;
            bits.push(`<li>${t('issue_' + i, { n: r.length || 0, chars: (r.confusable || []).join(', ') })}</li>`);
        }
        bits.push('</ul>');
        if (r.suggestion && r.suggestion.length === 17) {
            bits.push(`<div class="vd-card-b">${t('did_you_mean')}
                <button type="button" class="vd-fix" data-fix="${esc(r.suggestion)}">${esc(r.suggestion)}</button></div>`);
        }
        bits.push(`<div class="vd-fine">${t('invalid_hint')}</div>`);
        /* Номер кузова японской машины без дефиса неотличим от недонабранного
           VIN — подсказать формат, а не только «мало знаков». */
        if (r.issues.indexOf('too_short') >= 0 && r.length >= 8) bits.push(`<div class="vd-fine">${t('issue_frame_hint')}</div>`);
        bits.push('</div>');
        ui.out.innerHTML = bits.join('');
        ui.out.hidden = false;
        stage('invalid', { issues: r.issues.length });
        ae('feature_use', { action: 'decode_invalid', issues: r.issues.join(',') });
        return;
    }

    const plant = makerCountry(r.manufacturer);
    const country = plant || r.country;
    const rows = [];
    rows.push(`<div class="vd-row"><span class="vd-k">${t('f_country')}</span>
        <span class="vd-v">${country ? esc(t('country.' + country)) : esc(t('country_unknown'))}</span></div>`);
    if (plant && r.country && plant !== r.country) {
        rows.push(`<div class="vd-fine">${t('country_by_maker', { iso: esc(t('country.' + r.country)) })}</div>`);
    }
    rows.push(`<div class="vd-row"><span class="vd-k">${t('f_maker')}</span>
        <span class="vd-v">${r.manufacturer ? esc(formatMaker(r.manufacturer)) : esc(t('maker_unknown'))}</span></div>`);
    if (!r.manufacturer) rows.push(`<div class="vd-fine">${t('maker_unknown_hint', { wmi: r.wmi })}</div>`);
    else if (r.makerDetail && r.makerDetail.toLowerCase() !== String(r.manufacturer).split('|')[0].toLowerCase()) {
        /* Запись списка — цитата по-английски, с источником: в ней завод,
           модели и годы, которых в коротком имени нет. */
        rows.push(`<div class="vd-fine vd-quote">${t('maker_quote')} <span lang="en" dir="ltr">«${esc(r.makerDetail)}»</span></div>`);
    }
    rows.push(renderYear(r));
    rows.push(`<div class="vd-row"><span class="vd-k">${t('f_serial')}</span>
        <span class="vd-v vd-mono">${esc(r.serial)}</span></div>`);
    rows.push(`<div class="vd-fine">${t('f_serial_hint')}</div>`);

    ui.out.innerHTML = renderRuler([
        { cls: 'wmi', text: r.wmi, label: t('part_wmi') },
        { cls: 'vds', text: r.vds, label: t('part_vds') },
        { cls: 'chk', text: r.checkDigit, label: t('part_check') },
        { cls: 'yr', text: r.yearCode, label: t('part_year') },
        { cls: 'plant', text: r.plantCode, label: t('part_plant') },
        { cls: 'ser', text: r.serial, label: t('part_serial') },
    ])
        + renderChecksum(r)
        + `<div class="vd-facts">${rows.join('')}</div>`
        + `<div class="vd-card vd-neutral"><div class="vd-card-h">${t('vds_title')}</div>
            <div class="vd-card-b">${t('vds_body', { vds: r.vds })}</div></div>`
        + `<div class="vd-card vd-warn"><div class="vd-card-h">${t('not_in_vin_title')}</div>
            <div class="vd-card-b">${t('not_in_vin_body')}</div></div>`
        + renderSources();
    ui.out.hidden = false;

    stage('decode', { country: country || '', checksum: r.checksum.state, maker_known: r.manufacturer ? 1 : 0 });
    ae('feature_use', {
        action: 'decode', wmi: r.wmi, country: country || '',
        maker_known: r.manufacturer ? 1 : 0,
        data: hasWmiData() ? 1 : 0,
        checksum: r.checksum.state,
        cd_measured: r.checksum.measured ? 1 : 0,
        year_policy: r.year ? r.year.policy : '',
        year_certain: r.year && r.year.certain ? 1 : 0,
    });
}

/* ── «Мой гараж» ─────────────────────────────────────────────────────────────
   Машины гаража (аккаунт — /api/session-bootstrap, браузер — журнал /car-logbook)
   приходят в простое страницы через общий car-catalog/car-context.js. VIN владельца
   в разметке не бывает никогда: кнопка «Проверить VIN вашей машины» рисуется из
   ответа bootstrap. После разбора — «Это моя машина» / «Добавить в гараж»: марку и
   год (где год надёжен) даёт тот же справочник машин (catalog.fromVin), что и выбор
   машины «По VIN»; справочник грузится только по нажатию. docs/why/car-tools-one-system.md */
let CX = null;
let GAR = null;
let catalogP = null;

function catalog(url) {
    if (!catalogP) {
        catalogP = Promise.all([
            fetch(url || '/api/car-catalog', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
                .then((r) => { if (!r.ok) throw new Error('car-catalog HTTP ' + r.status); return r.json(); }),
            import('../car-catalog/catalog.js'),
        ]).then(([j, C]) => ({ cat: C.createCatalog(j), nameIn: C.nameIn })).catch((e) => { catalogP = null; throw e; });
    }
    return catalogP;
}

const vinOf = (gc) => normalize(gc.vin || '');
const escHtml = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

function renderMine() {
    const host = $('vdMine');
    if (!host || !GAR) return;
    const T = CX.strings();
    const mine = GAR.cars.filter((gc) => vinOf(gc).length === 17).slice(0, 5);
    host.innerHTML = mine.map((gc) => `<button type="button" class="cx-btn" data-mine="${escHtml(gc.key)}">🚗 ${escHtml(CX.tr(T, 'vin_check_mine', { car: gc.label }))}</button>`).join('');
    host.hidden = !mine.length;
}

/** After a decode: the garage's answer to this VIN. */
function renderGarageAct(raw) {
    const host = $('vdCar');
    if (!host || !CX) return;
    const r = raw ? decode(raw) : null;
    if (!r || !r.valid || r.kind === 'frame' || !r.vin) { host.hidden = true; host.innerHTML = ''; return; }
    const T = CX.strings();
    const same = GAR ? GAR.cars.find((gc) => vinOf(gc) === r.vin) : null;
    host.innerHTML = same
        ? `<p class="cx-note">🚗 ${escHtml(CX.tr(T, 'vin_has', { car: same.label }))}</p>`
        : `<div class="cx-act"><button type="button" class="cx-btn" data-vin-save>🚗 ${escHtml(CX.tr(T, 'vin_save'))}</button></div>`;
    host.hidden = false;
}

async function vinSaveStart(btn) {
    const host = $('vdCar');
    const T = CX.strings();
    const r = decode(lastRaw || '');
    if (!r || !r.valid) return;
    btn.disabled = true;
    let res = null, make = null, nameIn = null;
    try {
        const c = await catalog(host.dataset.catalog);
        res = c.cat.fromVin(r.vin, decode);
        make = res.makeId ? c.cat.make(res.makeId) : null;
        nameIn = c.nameIn;
    } catch (e) {
        btn.disabled = false;
        if (window.__handleError) window.__handleError(e, 'vin_garage_catalog', { say: CX.tr(T, 'vin_failed') });
        return;
    }
    const href = host.dataset.logbook || '';
    if (!make) {
        host.innerHTML = `<p class="cx-note">${escHtml(CX.tr(T, 'vin_no_make'))}${href ? ` <a href="${escHtml(href)}">${escHtml(CX.tr(T, 'j_open'))}</a>` : ''}</p>`;
        ae('feature_use', { action: 'garage_vin_no_make' });
        return;
    }
    const makeLabel = nameIn(make, ui.cfg.locale || window.__LOCALE || 'en');
    const cars = (GAR ? GAR.cars : []).filter((gc) => gc.makeId === res.makeId);
    const signedIn = !!(GAR && GAR.loggedIn);
    host.innerHTML = '<div class="cx-act">'
        + cars.map((gc) => `<button type="button" class="cx-btn" data-vin-to="${escHtml(gc.key)}">${escHtml(CX.tr(T, 'vin_mine', { car: gc.label + (gc.year ? ' ' + gc.year : '') }))}</button>`).join('')
        + `<button type="button" class="cx-btn" data-vin-to="">${escHtml(CX.tr(T, signedIn ? 'vin_add' : 'vin_add_guest', { make: makeLabel }))}</button>`
        + '</div>';
    host._vin = { vin: r.vin, makeId: res.makeId, makeLabel, year: res.year || null };
}

async function vinSaveTo(key) {
    const host = $('vdCar');
    const T = CX.strings();
    const v = host._vin;
    if (!v) return;
    const gc = key && GAR ? GAR.cars.find((x) => x.key === key) : null;
    host.querySelectorAll('button').forEach((b) => { b.disabled = true; });
    try {
        const out = await CX.saveVin(gc, { vin: v.vin, makeId: v.makeId, makeLabel: v.makeLabel, year: v.year, url: host.dataset.meCars, locale: ui.cfg.locale });
        if (gc) gc.vin = v.vin;
        const href = host.dataset.logbook || '';
        host.innerHTML = `<p class="cx-ok">✓ ${escHtml(CX.tr(T, 'vin_saved', { car: out.label || v.makeLabel }))}${gc ? '' : ' ' + escHtml(CX.tr(T, 'vin_model_next'))}${href ? ` <a href="${escHtml(href)}">${escHtml(CX.tr(T, 'j_open'))}</a>` : ''}</p>`;
        ae('feature_use', { action: gc ? 'garage_vin_to_car' : 'garage_vin_new_car', src: gc ? gc.src : (GAR && GAR.loggedIn ? 'acct' : 'lb') });
        renderMine();
    } catch (e) {
        host.querySelectorAll('button').forEach((b) => { b.disabled = false; });
        const first = e && e.errors ? Object.values(e.errors).flat()[0] : null;
        if (window.__handleError) window.__handleError(e, 'vin_garage_save', { say: first || CX.tr(T, 'vin_failed') });
    }
}

async function garage() {
    try { CX = await import('../car-catalog/car-context.js'); } catch (e) { return; }
    const mine = $('vdMine');
    if (mine) mine.addEventListener('click', (e) => {
        const b = e.target.closest && e.target.closest('[data-mine]');
        const gc = b && GAR ? GAR.cars.find((x) => x.key === b.dataset.mine) : null;
        if (!gc) return;
        ui.input.value = vinOf(gc);
        decodeNow(ui.input.value);
        ae('feature_use', { action: 'garage_vin_check', src: gc.src });
    });
    const host = $('vdCar');
    if (host) host.addEventListener('click', (e) => {
        const b = e.target.closest && e.target.closest('button');
        if (!b) return;
        if (b.hasAttribute('data-vin-save')) vinSaveStart(b);
        else if (b.hasAttribute('data-vin-to')) vinSaveTo(b.dataset.vinTo);
    });
    CX.whenGarage((g) => {
        GAR = g;
        renderMine();
        if (lastRaw) renderGarageAct(lastRaw);
    }, { locale: ui.cfg.locale || window.__LOCALE || 'en' });
}

/** Разобрать — дождавшись таблицы изготовителей (ограниченно). */
function decodeNow(raw) {
    if (hasWmiData()) { render(raw); return; }
    ui.go.disabled = true;
    waitData().then((ok) => {
        ui.go.disabled = false;
        render(raw);
        /* Таблица не успела — ответ дан по ядру; когда она дойдёт, тот же
           номер перерисуется с полными данными, без второго нажатия. */
        if (!ok) loadData().then((late) => { if (late && lastRaw === raw) render(raw); });
    });
}

export function mount(cfg) {
    ui = { cfg, lang: cfg.lang || {}, input: $('vdInput'), go: $('vdGo'), out: $('vdOut') };
    loadData();

    const run = () => {
        const v = normalize(ui.input.value);
        if (!v) {
            /* ⚠️ Раньше здесь стоял голый `return`, и на экране ОСТАВАЛСЯ разбор
               предыдущего номера. Человек очистил поле — а перед ним всё ещё
               чужой ответ; тост уходит через секунды, ответ остаётся. Пустой
               ввод обязан очищать вывод: показывать нечего. */
            ui.out.innerHTML = '';
            ui.out.hidden = true;
            lastRaw = null;
            renderGarageAct(null);
            const note = $('vdLive');
            if (note) note.hidden = true;
            window.__toast && window.__toast(t('empty'), 'warning');
            return;
        }
        decodeNow(ui.input.value);
    };

    ui.go.addEventListener('click', run);
    ui.input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); run(); } });

    /* Живая подсказка о недопустимых буквах — ДО нажатия: человек набирает VIN
       с таблички и почти всегда путает O с нулём, а узнаёт об этом сейчас, а не
       после отказа. Номер кузова японской машины — не «N из 17». */
    ui.input.addEventListener('input', () => {
        const v = normalize(ui.input.value);
        const bad = [...new Set([...v].filter((c) => CONFUSABLE[c]))];
        const note = $('vdLive');
        if (!note) return;
        if (frameNumber(ui.input.value)) {
            note.textContent = t('live_frame');
            note.hidden = false;
        } else if (bad.length) {
            note.textContent = t('live_confusable', { chars: bad.join(', ') });
            note.hidden = false;
        } else if (v.length && v.length !== 17) {
            note.textContent = t('live_length', { n: v.length });
            note.hidden = false;
        } else note.hidden = true;
    });

    ui.out.addEventListener('click', (e) => {
        const fix = e.target && e.target.dataset && e.target.dataset.fix;
        if (fix) { ui.input.value = fix; decodeNow(fix); ae('feature_use', { action: 'confusable_fixed' }); }
    });

    document.querySelectorAll('[data-vin-sample]').forEach((el) => {
        el.addEventListener('click', () => { ui.input.value = el.dataset.vinSample; decodeNow(el.dataset.vinSample); });
    });

    try {
        const q = new URLSearchParams(location.search).get('vin');
        if (q) { ui.input.value = q; decodeNow(q); }
    } catch (e) {}
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 600));
    if (document.readyState === 'complete') idle(garage, { timeout: 4000 });
    else window.addEventListener('load', () => idle(garage, { timeout: 4000 }), { once: true });
}

function boot() {
    const cfg = window.__VIN_CFG;
    if (!cfg || !document.getElementById('vdInput')) return;
    try { mount(cfg); } catch (e) {
        window.__handleError ? window.__handleError(e, 'vin_mount') : console.error(e);
    }
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
